/** @file guideSheetOf: the overpaint guide: the rest pose as flat coloured regions, joints, a legend. */
import { createImage } from '@zilo/pose3d-image-io/image/createImage';
import { fillRect } from '@zilo/pose3d-image-io/image/fillRect';
import { drawText } from '@zilo/pose3d-image-io/text/drawText';
import type { Image, Rgba } from '@zilo/pose3d-image-io/types/image';
import { guideOwnersOf } from './guideOwnersOf';
import { guideRegionsOf } from './guideRegionsOf';
import type { GuideRegion, GuideSheet, RestPlacement } from './sheetTypes';

const OUTLINE: Rgba = [0, 0, 0, 255];
const DOT: Rgba = [255, 255, 255, 255];
const STRIP: Rgba = [40, 42, 48, 255];
const TEXT: Rgba = [240, 240, 240, 255];
/** Legend text scale (3x5 glyphs become 6x10), row height, swatch size. */
const FONT = 2;
const ROW = 16;
const SWATCH = 12;

/** Owner of the 1x pixel under scaled pixel (x, y); -1 outside. */
const ownerAt = (owners: Int16Array, w: number, h: number, x: number, y: number, k: number): number => {
  const ox = Math.floor(x / k);
  const oy = Math.floor(y / k);
  return ox < 0 || oy < 0 || ox >= w || oy >= h ? -1 : (owners[oy * w + ox] ?? -1);
};

/** Each owned scaled pixel in its region's colour; a pixel with a 4-neighbour of another owner is the outline. */
function paintFigure(img: Image, owners: Int16Array, placement: RestPlacement, regions: readonly GuideRegion[], k: number): void {
  const { width: w, height: h } = placement;
  for (let y = 0; y < h * k; y++) {
    for (let x = 0; x < w * k; x++) {
      const o = ownerAt(owners, w, h, x, y, k);
      const region = regions[o];
      if (region === undefined) continue;
      const edge = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx = 0, dy = 0]) => ownerAt(owners, w, h, x + dx, y + dy, k) !== o);
      img.data.set(edge ? OUTLINE : region.color, (y * img.width + x) * 4);
    }
  }
}

/** A white 5x5 dot in a black ring at every region's pivot (the joint the part pins to). */
function paintJoints(img: Image, regions: readonly GuideRegion[], k: number): void {
  for (const r of regions) {
    const x = Math.round(r.part.pivotScreen[0] * k);
    const y = Math.round(r.part.pivotScreen[1] * k);
    fillRect(img, x - 3, y - 3, 7, 7, OUTLINE);
    fillRect(img, x - 2, y - 2, 5, 5, DOT);
  }
}

/** Width of one legend column for names of `chars` characters. */
const columnWidth = (chars: number): number => 4 + SWATCH + 6 + chars * 4 * FONT + 8;

/** The legend strip at `top`: a dark band, then per region a swatch of its colour and the drawable id, in columns. */
function paintLegend(img: Image, regions: readonly GuideRegion[], top: number): void {
  fillRect(img, 0, top, img.width, img.height - top, STRIP);
  const colW = columnWidth(Math.max(1, ...regions.map((r) => r.part.drawable.length)));
  const cols = Math.max(1, Math.floor(img.width / colW));
  regions.forEach((r, i) => {
    const x = (i % cols) * colW + 4;
    const y = top + 6 + Math.floor(i / cols) * ROW;
    fillRect(img, x, y, SWATCH, SWATCH, r.color);
    drawText(img, r.part.drawable, x + SWATCH + 6, y + 1, TEXT, FONT);
  });
}

/**
 * The guide of `placement` at `scale` (default 4): the figure (`placement` size times `scale`) with every region a flat
 * colour inside a 1 px black outline and a dot on each joint, over a transparent background; below it a legend strip
 * (colour -> drawable). `masks` (slot key -> the server's silhouette) narrow a region to the part's shape.
 */
export function guideSheetOf(placement: RestPlacement, masks: ReadonlyMap<string, Image> = new Map(), scale = 4): GuideSheet {
  const regions = guideRegionsOf(placement);
  const owners = guideOwnersOf(placement, regions, masks);
  const colW = columnWidth(Math.max(1, ...regions.map((r) => r.part.drawable.length)));
  const width = Math.max(placement.width * scale, colW + 8);
  const cols = Math.max(1, Math.floor(width / colW));
  const figureHeight = placement.height * scale;
  const image = createImage(width, figureHeight + 10 + Math.ceil(regions.length / cols) * ROW);
  paintFigure(image, owners, placement, regions, scale);
  paintJoints(image, regions, scale);
  paintLegend(image, regions, figureHeight);
  return { placement, regions, scale, owners, image, figureHeight };
}
